import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Card } from "@/components/ui/card";
import { fmtMoney, fmtNumber, fmtDate } from "@/lib/format";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid,
} from "recharts";
import {
  TrendingUp, TrendingDown, Wallet, Landmark, Coins, Smartphone, AlertTriangle, PackageX,
  ArrowUpRight, ArrowDownRight, Loader2, Trophy,
} from "lucide-react";

const TREASURY = [
  { key: "bank", label: "Banque", color: "#1E293B", icon: Landmark },
  { key: "cash", label: "Espèces", color: "#16A34A", icon: Coins },
  { key: "orange_money", label: "Orange Money", color: "#F97316", icon: Smartphone },
  { key: "moov_money", label: "Moov Money", color: "#2563EB", icon: Smartphone },
  { key: "wave_money", label: "Wave", color: "#06B6D4", icon: Smartphone },
];

function KpiCard({ label, value, sub, positive, testid }) {
  return (
    <Card className="p-5 rounded-md border-border shadow-sm" data-testid={testid}>
      <div className="text-sm text-muted-foreground">{label}</div>
      <div className="mt-1.5 font-display text-2xl font-bold tracking-tight tabular">{value}</div>
      {sub != null && (
        <div className={`mt-1 text-xs font-medium flex items-center gap-1 ${positive ? "text-emerald-600" : "text-rose-600"}`}>
          {positive ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />} {sub}
        </div>
      )}
    </Card>
  );
}

export default function Dashboard() {
  const { activeCompany, activeCompanyId } = useApp();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const cur = activeCompany?.base_currency || "XOF";

  useEffect(() => {
    if (!activeCompanyId) return;
    setLoading(true);
    api.get("/dashboard", { params: { company_id: activeCompanyId } })
      .then(({ data }) => setData(data))
      .finally(() => setLoading(false));
  }, [activeCompanyId]);

  if (loading || !data) {
    return <div className="flex items-center justify-center py-32"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  const f = data.finance;
  const c = data.cockpit || {};
  const chg = (v, suffix) => v == null ? null : `${v >= 0 ? "+" : ""}${v} % ${suffix}`;
  const donutData = TREASURY.map((t) => ({ name: t.label, value: data.treasury[t.key] || 0, color: t.color })).filter((d) => d.value > 0);

  return (
    <div className="space-y-6" data-testid="dashboard">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight">Bonjour {c.user_name || ""} 👋</h1>
        <p className="text-muted-foreground">Voici l'essentiel de <span className="font-semibold text-foreground">{data.company.name}</span> aujourd'hui.</p>
      </div>

      {c.score != null && (
        <Card className="p-5 rounded-md border-border shadow-sm" data-testid="natoriq-score">
          <div className="flex flex-col sm:flex-row sm:items-center gap-5">
            <div className="flex items-center gap-4">
              <div className="relative w-20 h-20 rounded-full flex items-center justify-center" style={{ background: `conic-gradient(#EA580C ${c.score * 3.6}deg, #F1F5F9 0deg)` }}>
                <div className="rounded-full bg-white flex flex-col items-center justify-center" style={{ width: 60, height: 60 }}>
                  <span className="font-display text-xl font-bold leading-none">{c.score}</span>
                  <span className="text-[9px] text-muted-foreground">/ 100</span>
                </div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">NATORIQ Business Health</div>
                <div className="font-display text-lg font-bold">{c.score_label}</div>
              </div>
            </div>
            <div className="flex-1 flex flex-wrap gap-2">
              {(c.score_factors || []).map((fac) => (
                <span key={fac.key} className="inline-flex items-center gap-1.5 text-xs bg-secondary rounded-full px-3 py-1.5" data-testid={`score-factor-${fac.key}`}>
                  <span className="w-2 h-2 rounded-full" style={{ background: fac.status === "green" ? "#16A34A" : fac.status === "orange" ? "#F59E0B" : "#DC2626" }} />
                  {fac.label} · {fac.detail}
                </span>
              ))}
            </div>
          </div>
        </Card>
      )}

      {/* KPI row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard testid="kpi-revenue-today" label="CA aujourd'hui" value={fmtMoney(f.revenue_today, cur)}
          sub={chg(c.revenue_today_change, "vs hier")} positive={(c.revenue_today_change ?? 0) >= 0} />
        <KpiCard testid="kpi-revenue-month" label="CA ce mois" value={fmtMoney(f.revenue_month, cur)}
          sub={chg(c.revenue_month_change, "vs mois dernier")} positive={(c.revenue_month_change ?? 0) >= 0} />
        <KpiCard testid="kpi-net-profit" label="Bénéfice net (mois)" value={fmtMoney(f.net_profit, cur)}
          sub={f.net_profit >= 0 ? "Rentable" : "Déficit"} positive={f.net_profit >= 0} />
        <KpiCard testid="kpi-margin" label="Marge moyenne" value={`${f.margin} %`} />
      </div>

      {/* Chart + treasury */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 p-5 rounded-md border-border shadow-sm" data-testid="revenue-chart">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-lg font-semibold">Chiffre d'affaires (30 jours)</h2>
            <TrendingUp className="w-5 h-5 text-primary" />
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={data.revenue_trend} margin={{ left: -10, right: 10, top: 5 }}>
              <defs>
                <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#EA580C" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#EA580C" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#94A3B8" }} tickFormatter={(d) => d?.slice(8)} />
              <YAxis tick={{ fontSize: 11, fill: "#94A3B8" }} tickFormatter={(v) => `${Math.round(v / 1000)}k`} width={44} />
              <Tooltip formatter={(v) => fmtMoney(v, cur)} labelFormatter={(l) => fmtDate(l)} />
              <Area type="monotone" dataKey="amount" stroke="#EA580C" strokeWidth={2.5} fill="url(#rev)" />
            </AreaChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-5 rounded-md border-border shadow-sm" data-testid="treasury-widget">
          <div className="flex items-center justify-between mb-2">
            <h2 className="font-display text-lg font-semibold">Trésorerie</h2>
            <Wallet className="w-5 h-5 text-primary" />
          </div>
          <div className="text-2xl font-display font-bold tabular">{fmtMoney(data.treasury.total, cur)}</div>
          <div className="text-xs text-muted-foreground mb-1">Total disponible</div>
          {c.treasury_runway_days != null && (
            <div className="text-xs font-medium text-primary mb-2" data-testid="treasury-runway">Autonomie de trésorerie : {c.treasury_runway_days} jours</div>
          )}
          <ResponsiveContainer width="100%" height={140}>
            <PieChart>
              <Pie data={donutData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={40} outerRadius={62} paddingAngle={2}>
                {donutData.map((d, i) => <Cell key={i} fill={d.color} />)}
              </Pie>
              <Tooltip formatter={(v) => fmtMoney(v, cur)} />
            </PieChart>
          </ResponsiveContainer>
          <div className="space-y-1.5 mt-2">
            {TREASURY.map((t) => (
              <div key={t.key} className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: t.color }} />
                  {t.label}
                </span>
                <span className="tabular font-medium">{fmtMoney(data.treasury[t.key] || 0, cur)}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Secondary KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <KpiCard testid="kpi-receivables" label="Créances clients" value={fmtMoney(f.receivables, cur)} />
        <KpiCard testid="kpi-payables" label="Dettes fournisseurs" value={fmtMoney(f.payables, cur)} />
        <KpiCard testid="kpi-expenses" label="Dépenses du mois" value={fmtMoney(f.expenses_month, cur)} />
      </div>

      {/* Lists */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="p-5 rounded-md border-border shadow-sm" data-testid="overdue-invoices">
          <h2 className="font-display text-lg font-semibold flex items-center gap-2 mb-4">
            <AlertTriangle className="w-5 h-5 text-rose-500" /> Factures en retard
          </h2>
          {data.overdue_invoices.length === 0 ? (
            <p className="text-sm text-muted-foreground">Aucune facture en retard 🎉</p>
          ) : (
            <div className="space-y-3">
              {data.overdue_invoices.map((i) => (
                <div key={i.id} className="flex items-center justify-between text-sm border-b border-border pb-2 last:border-0">
                  <div>
                    <div className="font-medium">{i.customer}</div>
                    <div className="text-xs text-muted-foreground">{i.number} · échéance {fmtDate(i.due_date)}</div>
                  </div>
                  <span className="tabular font-semibold text-rose-600">{fmtMoney(i.amount, cur)}</span>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card className="p-5 rounded-md border-border shadow-sm" data-testid="low-stock">
          <h2 className="font-display text-lg font-semibold flex items-center gap-2 mb-4">
            <PackageX className="w-5 h-5 text-amber-500" /> Produits bientôt épuisés
          </h2>
          {data.stock.low_stock.length === 0 ? (
            <p className="text-sm text-muted-foreground">Aucune alerte de stock.</p>
          ) : (
            <div className="space-y-3">
              {data.stock.low_stock.map((p) => (
                <div key={p.id} className="flex items-center justify-between text-sm border-b border-border pb-2 last:border-0">
                  <span className="font-medium">{p.name}</span>
                  <span className="tabular text-amber-600 font-semibold">{fmtNumber(p.quantity)} restant(s)</span>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card className="p-5 rounded-md border-border shadow-sm" data-testid="top-customers">
          <h2 className="font-display text-lg font-semibold flex items-center gap-2 mb-4">
            <Trophy className="w-5 h-5 text-primary" /> Meilleurs clients
          </h2>
          {data.commercial.top_customers.length === 0 ? (
            <p className="text-sm text-muted-foreground">Pas encore de ventes.</p>
          ) : (
            <div className="space-y-3">
              {data.commercial.top_customers.map((c, i) => (
                <div key={i} className="flex items-center justify-between text-sm border-b border-border pb-2 last:border-0">
                  <span className="font-medium flex items-center gap-2"><span className="text-muted-foreground w-4">{i + 1}.</span>{c.name}</span>
                  <span className="tabular font-semibold">{fmtMoney(c.amount, cur)}</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
